/**
 * UserManagement component - handles staff user management and sales statistics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, formatIST } from '../../lib/store';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { UserPlus, Users, Trash2, ChevronRight, Lock, Settings, Monitor, ShieldOff } from 'lucide-react';

import { useAuth } from '../../lib/store';
import { logger } from '../../lib/logger';

export function UserManagement() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Managers see the Staff tab but can ONLY create operational roles (staff /
  // creator / dispatcher / delivery / stock_manager). Admin/manager creation
  // stays admin-only — enforced on backend too via /auth/register.
  const { user: currentUser } = useAuth();
  const isManagerOnly = currentUser?.role === 'manager';
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'staff', staff_code: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [salesStats, setSalesStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Edit user state
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', staff_code: '', phone: '', role: '', email: '', new_email: '', password: '' });
  
  // Client-side staff-row filter (name / email / staff_code). Empty = show all.
  const [staffSearch, setStaffSearch] = useState('');
  
  // Passcode protection state. The passcode itself is checked on the
  // SERVER (X-Action-Passcode header) — the browser never compares it.
  // We stash the code in memory for the duration of a single action so
  // the user only enters it once even though the flow spans two dialogs
  // (passcode → form → server-side action).
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcodeInput, setPasscodeInput] = useState('');
  const [passcodeAction, setPasscodeAction] = useState(null); // 'add' | 'delete' | 'edit'
  const [pendingPasscode, setPendingPasscode] = useState('');
  
  // Passcode settings (Admin only)
  const [showPasscodeSettings, setShowPasscodeSettings] = useState(false);
  const [currentPasscode, setCurrentPasscode] = useState('');
  // "Forgot the passcode?" lane — admin re-enters their own login password
  // instead of the current passcode. Server verifies against the caller's
  // own password_hash via the verify_password helper.
  const [forgotPasscode, setForgotPasscode] = useState(false);
  const [adminLoginPassword, setAdminLoginPassword] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  
  // Request passcode before action
  const requestPasscode = (action) => {
    setPasscodeAction(action);
    setPasscodeInput('');
    setShowPasscodeDialog(true);
  };
  
  // Stash the entered code and open the next step for the action. The
  // server does the real verification — a wrong code surfaces as a 403
  // toast once the user clicks the final Create / Delete / Save button.
  const verifyPasscode = () => {
    if (!passcodeInput) return;
    setPendingPasscode(passcodeInput);
    setShowPasscodeDialog(false);
    setPasscodeInput('');
    if (passcodeAction === 'add') {
      setShowDialog(true);
    } else if (passcodeAction === 'delete' && userToDelete) {
      handleDeleteUser(passcodeInput);
    } else if (passcodeAction === 'edit') {
      handleSaveEdit(passcodeInput);
    }
  };
  
  // Change the server-side action passcode. Local checks are just UX
  // (length + match); the server also enforces >=4 and, when a passcode
  // is already stored, re-verifies the current one. Current-passcode
  // field is OPTIONAL because on first-time setup nothing is stored.
  const handleChangePasscode = async () => {
    if (newPasscode.length < 4) {
      toast.error('New passcode must be at least 4 characters');
      return;
    }
    if (newPasscode !== confirmPasscode) {
      toast.error('New passcodes do not match');
      return;
    }
    try {
      await api.changeActionPasscode(
        forgotPasscode ? '' : currentPasscode,
        newPasscode,
        forgotPasscode ? adminLoginPassword : '',
      );
      toast.success('Passcode updated successfully');
      setShowPasscodeSettings(false);
      setCurrentPasscode('');
      setAdminLoginPassword('');
      setForgotPasscode(false);
      setNewPasscode('');
      setConfirmPasscode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update passcode');
    }
  };

  const fetchSalesStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await api.getSalesStats({});
      setSalesStats(res.data);
    } catch (err) {
      logger.error('Failed to fetch sales stats:', err);
    }
    setLoadingStats(false);
  }, []);

  useEffect(() => {
    fetchSalesStats();
  }, [fetchSalesStats]);

  // Auto-expand a specific staff card when arriving here via
  // "Back" from the Quotation Detail page. The QuotationDetail Back
  // button navigates to /admin?tab=staff&expandUser=<userId> so the
  // staff-member's card re-opens exactly where they left off. The
  // param is consumed once, then stripped from the URL so a subsequent
  // manual collapse doesn't get re-expanded on re-render.
  useEffect(() => {
    const expandUserId = searchParams.get('expandUser');
    if (expandUserId) {
      setExpandedUser(expandUserId);
      // Drop the param so the URL isn't "sticky" — next time the tab
      // opens without it, users start clean.
      const next = new URLSearchParams(searchParams);
      next.delete('expandUser');
      setSearchParams(next, { replace: true });
      // Scroll the card into view once the list has rendered.
      setTimeout(() => {
        const el = document.querySelector(`[data-user-card-id="${expandUserId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, [searchParams, setSearchParams]);

  const handleDeleteUser = async (passcode = pendingPasscode) => {
    if (!userToDelete) return;
    try {
      await api.deleteUser(userToDelete.user_id, passcode);
      toast.success(`${userToDelete.user_name} has been deleted`);
      setUserToDelete(null);
      setPendingPasscode('');
      fetchSalesStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };
  
  // Initiate delete with passcode
  const initiateDelete = (user) => {
    setUserToDelete(user);
    requestPasscode('delete');
  };

  const [credentialsToShare, setCredentialsToShare] = useState(null);

  const handleCreate = async () => {
    if (!form.email || !form.password || !form.name) { 
      toast.error('All fields are required'); 
      return; 
    }
    setSaving(true);
    try {
      await api.register(form, pendingPasscode);
      toast.success('User created successfully');
      // Capture for credential-share dialog before resetting
      setCredentialsToShare({
        name: form.name,
        email: form.email,
        password: form.password,
        role: form.role,
      });
      setShowDialog(false);
      setForm({ name: '', email: '', password: '', role: 'staff', staff_code: '', phone: '' });
      setPendingPasscode('');
      fetchSalesStats();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to create user'); 
    } finally { 
      setSaving(false); 
    }
  };

  const buildCredentialMessage = (creds) => {
    if (!creds) return '';
    const url = (typeof window !== 'undefined') ? window.location.origin : 'https://hedaplyquotations.com';
    return (
      `Hi ${creds.name},\n\n` +
      `Your login for HEDA PLYWOODS Quotation App:\n\n` +
      `🔗 Link: ${url}\n` +
      `📧 Email: ${creds.email}\n` +
      `🔐 Password: ${creds.password}\n` +
      `👤 Role: ${creds.role}\n\n` +
      `Please keep these details safe. You can change your password after first login.\n\n` +
      `— HEDA PLYWOODS`
    );
  };

  const handleCopyCredentials = async () => {
    const text = buildCredentialMessage(credentialsToShare);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Credentials copied! Paste in WhatsApp/Email');
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Credentials copied!');
    }
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(buildCredentialMessage(credentialsToShare));
    const phone = credentialsToShare?.phone?.replace(/\D/g, '') || '';
    const url = phone ? `https://wa.me/${phone.startsWith('91') ? phone : '91' + phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };
  
  // Handle editing existing user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name || '',
      staff_code: user.staff_code || '',
      phone: user.phone || '',
      role: user.role || 'staff',
      email: user.email || '',
      // Fresh blank on every open — treated exactly like the New
      // Password field ('leave blank to keep').
      new_email: '',
      password: '',
    });
  };
  
  const handleSaveEdit = async (passcode = pendingPasscode) => {
    if (!editingUser) return;
    // Only ship fields that actually have a value — sending password=""
    // would attempt to set an empty password (rejected by backend).
    const payload = {
      name: editForm.name,
      staff_code: editForm.staff_code,
      phone: editForm.phone,
    };
    // Only ship role when it actually changed — otherwise the backend
    // would treat a no-op role field as a role change and demand the
    // action passcode on plain name/phone/email edits.
    if (editForm.role !== editingUser.role) payload.role = editForm.role;
    const nextEmail = (editForm.new_email || '').trim();
    if (nextEmail && nextEmail !== editingUser.email) payload.email = nextEmail;
    if (editForm.password && editForm.password.length >= 4) payload.password = editForm.password;

    // Role change OR non-empty new password require the
    // server-side action passcode. Name / staff code / phone / email
    // only edits skip the prompt. If we don't already have a code in
    // hand (from a previous passcode dialog in this action), open the
    // passcode dialog first — verifyPasscode will call us back with
    // the code once collected.
    const needsPasscode =
      editForm.role !== editingUser.role ||
      (editForm.password && editForm.password.length >= 4);
    if (needsPasscode && !passcode) {
      requestPasscode('edit');
      return;
    }

    try {
      await api.updateUser(editingUser.id, payload, passcode);
      toast.success('User updated successfully');
      setEditingUser(null);
      setPendingPasscode('');
      fetchSalesStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const formatDate = (isoString) => formatIST(isoString);

  return (
    <div className="space-y-6">
      {/* Add User Button + Settings */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2">
          <Button 
            onClick={() => requestPasscode('add')} 
            className="bg-[#556B2F] hover:bg-[#465926] text-white text-xs uppercase tracking-wider" 
            data-testid="admin-add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-1" /> Add Staff User
          </Button>
          
          {/* Passcode Settings Button */}
          <Button 
            onClick={() => setShowPasscodeSettings(true)} 
            variant="outline"
            className="text-xs border-[#E2E6D9] hover:bg-[#F2F4EF]" 
            data-testid="passcode-settings-btn"
          >
            <Settings className="w-4 h-4 mr-1" /> Passcode
          </Button>
        </div>
      </div>

      {/* Sales by User */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-[#1A1C16] uppercase tracking-wide">Quotations by Salesperson</h3>
          {/* Staff row filter — client-side match against name / email /
              staff_code. Empty string shows every staff member. */}
          <Input
            value={staffSearch}
            onChange={(e) => setStaffSearch(e.target.value)}
            placeholder="Search staff by name, email or code"
            className="h-8 text-xs w-full sm:w-64 bg-white border-[#E2E6D9]"
            data-testid="staff-search-input"
          />
        </div>
        {loadingStats ? (
          <p className="text-sm text-[#8F9485]">Loading...</p>
        ) : (() => {
          const rows = salesStats?.by_user || [];
          const needle = staffSearch.trim().toLowerCase();
          const filtered = needle
            ? rows.filter(u => {
                const code = (u.user_info?.staff_code || '').toLowerCase();
                const name = (u.user_name || '').toLowerCase();
                const email = (u.user_email || '').toLowerCase();
                return name.includes(needle) || email.includes(needle) || code.includes(needle);
              })
            : rows;
          if (filtered.length === 0) {
            return (
              <p className="text-sm text-[#8F9485]" data-testid="staff-empty-hint">
                {needle ? `No staff matches "${staffSearch}"` : 'No quotations found'}
              </p>
            );
          }
          return (
            <div className="space-y-3">
              {filtered.map((userStat) => (
                <UserStatCard 
                  key={userStat.user_id}
                  userStat={userStat}
                  expandedUser={expandedUser}
                  setExpandedUser={setExpandedUser}
                  initiateDelete={initiateDelete}
                  onEditUser={handleEditUser}
                  navigate={navigate}
                  formatDate={formatDate}
                />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Add User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm" data-testid="add-user-dialog">
          <DialogHeader><DialogTitle>Add Staff User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 text-sm" data-testid="user-form-name" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1 text-sm" data-testid="user-form-email" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1 text-sm" data-testid="user-form-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Staff Code (2 letters)</Label>
                <Input 
                  value={form.staff_code} 
                  onChange={e => setForm(f => ({ ...f, staff_code: e.target.value.toUpperCase().slice(0, 2) }))} 
                  className="mt-1 text-sm uppercase" 
                  placeholder="e.g., SJ"
                  maxLength={2}
                  data-testid="user-form-staff-code" 
                />
              </div>
              <div>
                <Label className="text-xs">Phone Number</Label>
                <Input 
                  value={form.phone} 
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} 
                  className="mt-1 text-sm" 
                  placeholder="9876543210"
                  data-testid="user-form-phone" 
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1 text-sm" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff (Sales - Quotations only)</SelectItem>
                  <SelectItem value="creator">Creator (Creates quotations for salespeople)</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher (Delivery + Stock + Quotations across the shop)</SelectItem>
                  <SelectItem value="delivery">Delivery (Delivery slips only)</SelectItem>
                  <SelectItem value="stock_manager">Stock Manager (Stock + Purchase Orders)</SelectItem>
                  {!isManagerOnly && <SelectItem value="manager">Manager (Products + Quotations)</SelectItem>}
                  {!isManagerOnly && <SelectItem value="admin">Admin (Full access)</SelectItem>}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#8F9485] mt-1">
                Staff: Create quotations • Creator: Creates for others • Manager: Products + Quotations • Admin: Full access
              </p>
            </div>
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-[#556B2F] hover:bg-[#465926] text-white" data-testid="user-form-submit">
              {saving ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User - {editingUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="mt-1 text-sm" data-testid="edit-user-name" />
            </div>
            <div>
              <Label className="text-xs">Login Email</Label>
              <Input
                type="email"
                value={editForm.email}
                readOnly
                disabled
                className="mt-1 text-sm bg-[#F2F4EF] cursor-not-allowed text-[#5C6150]"
                data-testid="edit-user-current-email"
              />
              <p className="text-[10px] text-[#8F9485] mt-1">
                Current login. All history and quotations stay linked to this user regardless of email changes.
              </p>
            </div>
            <div>
              <Label className="text-xs">New Email (leave blank to keep)</Label>
              <Input
                type="email"
                value={editForm.new_email}
                onChange={e => setEditForm(f => ({ ...f, new_email: e.target.value }))}
                className="mt-1 text-sm"
                autoComplete="off"
                data-testid="edit-user-new-email"
              />
            </div>
            <div>
              <Label className="text-xs">New Password (leave blank to keep)</Label>
              <Input
                type="text"
                value={editForm.password}
                onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                className="mt-1 text-sm font-mono"
                autoComplete="new-password"
                data-testid="edit-user-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Staff Code</Label>
                <Input 
                  value={editForm.staff_code} 
                  onChange={e => setEditForm(f => ({ ...f, staff_code: e.target.value.toUpperCase().slice(0, 2) }))} 
                  className="mt-1 text-sm uppercase" 
                  placeholder="e.g., SJ"
                  maxLength={2}
                />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input 
                  value={editForm.phone} 
                  onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} 
                  className="mt-1 text-sm" 
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="creator">Creator</SelectItem>
                  <SelectItem value="dispatcher">Dispatcher (Delivery + Stock + Quotations)</SelectItem>
                  {!isManagerOnly && <SelectItem value="manager">Manager</SelectItem>}
                  {!isManagerOnly && (
                    <SelectItem value="admin" disabled={editingUser?.role !== 'admin'}>Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setEditingUser(null)}>Cancel</Button>
              <Button className="flex-1 bg-[#556B2F] hover:bg-[#465926] text-white" onClick={() => handleSaveEdit()}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      {/* Passcode Verification Dialog */}
      <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
        <DialogContent className="max-w-xs" data-testid="passcode-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#556B2F]" />
              Enter Passcode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {passcodeAction === 'add' ? 'Enter passcode to add a new user' : 'Enter passcode to delete this user'}
            </p>
            <Input
              type="password"
              placeholder="Enter passcode"
              value={passcodeInput}
              onChange={e => setPasscodeInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && verifyPasscode()}
              className="text-center text-lg tracking-widest"
              data-testid="passcode-input"
              autoFocus
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPasscodeDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#556B2F] hover:bg-[#465926]" onClick={verifyPasscode}>
                Verify
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Passcode Settings Dialog (Admin Only) */}
      <Dialog open={showPasscodeSettings} onOpenChange={setShowPasscodeSettings}>
        <DialogContent className="max-w-sm" data-testid="passcode-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#556B2F]" />
              Change Passcode
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">
                  {forgotPasscode ? 'Your login password' : 'Current Passcode'}
                </Label>
                <button
                  type="button"
                  data-testid="forgot-passcode-toggle"
                  onClick={() => {
                    setForgotPasscode(v => !v);
                    setCurrentPasscode('');
                    setAdminLoginPassword('');
                  }}
                  className="text-xs text-[#556B2F] hover:underline"
                >
                  {forgotPasscode ? 'Use current passcode' : 'Forgot the passcode?'}
                </button>
              </div>
              {forgotPasscode ? (
                <Input
                  type="password"
                  data-testid="admin-login-password-input"
                  value={adminLoginPassword}
                  onChange={e => setAdminLoginPassword(e.target.value)}
                  placeholder="Enter your account login password"
                  className="mt-1"
                />
              ) : (
                <Input
                  type="password"
                  value={currentPasscode}
                  onChange={e => setCurrentPasscode(e.target.value)}
                  placeholder="Enter current passcode"
                  className="mt-1"
                />
              )}
            </div>
            <div>
              <Label className="text-xs">New Passcode</Label>
              <Input
                type="password"
                value={newPasscode}
                onChange={e => setNewPasscode(e.target.value)}
                placeholder="Enter new passcode (min 4 chars)"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Confirm New Passcode</Label>
              <Input
                type="password"
                value={confirmPasscode}
                onChange={e => setConfirmPasscode(e.target.value)}
                placeholder="Confirm new passcode"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPasscodeSettings(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#556B2F] hover:bg-[#465926]" onClick={handleChangePasscode}>
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credentials Share Dialog */}
      <Dialog open={!!credentialsToShare} onOpenChange={(open) => !open && setCredentialsToShare(null)}>
        <DialogContent className="bg-[#FFFBF0] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#556B2F] flex items-center gap-2">
              ✓ User Created Successfully
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5C6150]">
              Share these login details with <strong>{credentialsToShare?.name}</strong> via WhatsApp or any messenger:
            </p>
            <pre className="bg-white border border-[#E2E6D9] rounded-lg p-3 text-xs whitespace-pre-wrap font-mono text-[#1A1C16] max-h-60 overflow-auto" data-testid="credentials-message">{buildCredentialMessage(credentialsToShare)}</pre>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleCopyCredentials}
                className="bg-[#556B2F] hover:bg-[#465926] text-white"
                data-testid="copy-credentials-btn"
              >
                📋 Copy Message
              </Button>
              <Button
                onClick={handleShareWhatsApp}
                className="bg-[#25D366] hover:bg-[#1DA851] text-white"
                data-testid="share-whatsapp-btn"
              >
                💬 Send on WhatsApp
              </Button>
            </div>
            <Button
              onClick={() => setCredentialsToShare(null)}
              variant="outline"
              className="w-full"
              data-testid="dismiss-credentials-btn"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sub-component for user stat card
function UserStatCard({ userStat, expandedUser, setExpandedUser, initiateDelete, onEditUser, navigate, formatDate }) {
  const isExpanded = expandedUser === userStat.user_id;
  const userInfo = userStat.user_info || {};
  // Local state for the sessions drawer — kept inside the card so opening
  // one user's session list doesn't reset another card's quotation expansion.
  const [showSessions, setShowSessions] = useState(false);
  const [sessions, setSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // Per-expansion status filter for the quotation grid — 'all' | 'closed'
  // | 'pending' | 'lost'. Reset to 'all' whenever the card collapses so
  // the next expansion starts fresh (per spec).
  const [statusFilter, setStatusFilter] = useState('all');
  useEffect(() => {
    if (!isExpanded) setStatusFilter('all');
  }, [isExpanded]);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const res = await api.getUserSessions(userStat.user_id);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  const toggleSessions = async (e) => {
    e.stopPropagation();
    const next = !showSessions;
    setShowSessions(next);
    if (next && !sessions) await loadSessions();
  };

  const handleRevoke = async (sessionId) => {
    if (!window.confirm('Force sign-out this device? They will be logged out immediately.')) return;
    try {
      await api.revokeSession(sessionId);
      toast.success('Session revoked');
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm(`Force sign-out ${userStat.user_name} from ALL devices? They'll have to log in again everywhere.`)) return;
    try {
      const res = await api.revokeAllUserSessions(userStat.user_id);
      toast.success(`Revoked ${res.data?.revoked_count || 0} session(s)`);
      await loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Revoke failed');
    }
  };

  return (
    <div className="bg-white border border-[#E2E6D9] rounded-lg overflow-hidden" data-user-card-id={userStat.user_id}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 hover:bg-[#F5F6F0]">
        <button
          className="flex-1 min-w-0 flex items-center gap-3 text-left"
          onClick={() => setExpandedUser(isExpanded ? null : userStat.user_id)}
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-[#556B2F]/10 flex items-center justify-center">
            {userInfo.staff_code ? (
              <span className="text-sm font-bold text-[#556B2F]">{userInfo.staff_code}</span>
            ) : (
              <Users className="w-5 h-5 text-[#556B2F]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-[#1A1C16] truncate">
              {userStat.user_name}
              {userInfo.staff_code && (
                <span className="ml-2 text-xs font-normal bg-[#556B2F]/10 px-2 py-0.5 rounded text-[#556B2F]">
                  {userInfo.staff_code}
                </span>
              )}
            </p>
            <p className="text-xs text-[#8F9485] truncate">
              <span className="truncate">{userStat.user_email}</span>
              {userInfo.phone && <span className="ml-2">• {userInfo.phone}</span>}
              {userInfo.role && <span className="ml-2 capitalize">• {userInfo.role}</span>}
            </p>
          </div>
        </button>
        <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 pl-13 sm:pl-0 shrink-0">
          <button
            className="text-left sm:text-right"
            onClick={() => setExpandedUser(isExpanded ? null : userStat.user_id)}
          >
            <p className="font-mono-data font-semibold text-[#556B2F] text-sm sm:text-base whitespace-nowrap">
              {userStat.total_quotations} <span className="text-[10px] sm:text-xs font-normal">Quotations</span>
            </p>
            <p className="text-xs font-mono-data text-[#DAA520] whitespace-nowrap">₹{userStat.total_amount.toLocaleString('en-IN')}</p>
          </button>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="text-[#556B2F] hover:text-[#465926] hover:bg-[#556B2F]/10 h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              onEditUser({ id: userStat.user_id, name: userStat.user_name, ...userInfo });
            }}
            title="Edit User"
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-[#5C6150] hover:text-[#1A1C16] hover:bg-[#F5F6F0] h-8 w-8 p-0"
            onClick={toggleSessions}
            title="Recent logins & devices"
            data-testid={`sessions-toggle-${userStat.user_id}`}
          >
            <Monitor className="w-4 h-4" />
          </Button>
          {!userStat.user_email?.includes('admin') && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
              onClick={(e) => {
                e.stopPropagation();
                initiateDelete(userStat);
              }}
              data-testid={`delete-user-${userStat.user_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          </div>
        </div>
      </div>
      
      {/* Login sessions drawer — admin can spot suspicious devices and
          force sign-out a stolen phone without changing the password. */}
      {showSessions && (
        <div className="border-t border-[#E2E6D9] bg-[#F9FAF7] p-3" data-testid={`sessions-drawer-${userStat.user_id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#5C6150]">Recent logins & devices</p>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
              onClick={handleRevokeAll}
              data-testid={`revoke-all-${userStat.user_id}`}
            >
              <ShieldOff className="w-3 h-3 mr-1" />
              Sign out everywhere
            </Button>
          </div>
          {sessionsLoading ? (
            <p className="text-xs text-[#8F9485]">Loading…</p>
          ) : !sessions || sessions.length === 0 ? (
            <p className="text-xs text-[#8F9485]">No login history yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {sessions.map((s) => (
                <div
                  key={s.id}
                  className={
                    'flex items-center justify-between gap-2 px-2 py-1.5 rounded text-xs ' +
                    (s.revoked ? 'bg-red-50 text-[#8F9485] line-through' : 'bg-white')
                  }
                  data-testid={`session-row-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {s.device || 'Unknown device'}
                      {s.revoked && <span className="ml-2 not-italic no-underline text-red-700 font-semibold">REVOKED</span>}
                    </p>
                    <p className="text-[10px] text-[#8F9485] truncate">
                      {s.ip || 'unknown ip'} • {formatIST(s.created_at)}
                    </p>
                  </div>
                  {!s.revoked && (
                    <button
                      type="button"
                      onClick={() => handleRevoke(s.id)}
                      className="text-red-600 hover:text-red-800 text-[11px] font-semibold shrink-0"
                      data-testid={`revoke-${s.id}`}
                    >
                      Sign out
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expanded Quotations Grid */}
      {isExpanded && (
        <div className="border-t border-[#E2E6D9] bg-[#F9FAF7] p-3">
          {userStat.quotations.length === 0 ? (
            <p className="text-sm text-[#8F9485] text-center py-4">No quotations yet</p>
          ) : (() => {
            // Normalise status once so chips + filter agree on the same
            // rule: only 'closed' and 'lost' are recognised outcomes;
            // ANYTHING ELSE (null, empty, legacy values like 'draft' /
            // 'sent' / 'expired', or off-menu strings) folds into
            // 'pending'. This keeps the count and the filter using the
            // exact same value, so the Pending badge can never claim
            // rows the filter then hides.
            const withStatus = userStat.quotations
              .map(q => {
                const raw = (q.status || '').toString().toLowerCase();
                const norm = (raw === 'closed' || raw === 'lost') ? raw : 'pending';
                return { ...q, _norm_status: norm };
              })
              // Newest-first regardless of which filter chip
              // is active. Sort on the normalised list so All / Closed /
              // Pending / Lost tabs all read top-down chronologically.
              .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
            const counts = { all: withStatus.length, closed: 0, pending: 0, lost: 0 };
            withStatus.forEach(q => { counts[q._norm_status] += 1; });
            const chips = [
              { key: 'all',     label: 'All',     dot: 'bg-[#8F9485]',  active: 'bg-[#8F9485]/10 border-[#8F9485]/40 text-[#5C6150]' },
              { key: 'closed',  label: 'Closed',  dot: 'bg-[#556B2F]',  active: 'bg-[#556B2F]/10 border-[#556B2F]/40 text-[#3D5320]' },
              { key: 'pending', label: 'Pending', dot: 'bg-[#DAA520]',  active: 'bg-[#DAA520]/10 border-[#DAA520]/40 text-[#8B6914]' },
              { key: 'lost',    label: 'Lost',    dot: 'bg-[#C44A4A]',  active: 'bg-[#C44A4A]/10 border-[#C44A4A]/40 text-[#8B2E2E]' },
            ];
            const visible = statusFilter === 'all'
              ? withStatus
              : withStatus.filter(q => q._norm_status === statusFilter);
            return (
              <>
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`staff-status-filter-${userStat.user_id}`}>
                  {chips.map(c => {
                    const isActive = statusFilter === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setStatusFilter(c.key)}
                        className={
                          'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-medium transition-colors ' +
                          (isActive ? c.active : 'bg-white border-[#E2E6D9] text-[#5C6150] hover:border-[#8F9485]')
                        }
                        data-testid={`staff-status-chip-${c.key}-${userStat.user_id}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                        {c.label} <span className="text-[10px] text-[#8F9485]">{counts[c.key]}</span>
                      </button>
                    );
                  })}
                </div>
                {visible.length === 0 ? (
                  <p className="text-xs text-[#8F9485] text-center py-3" data-testid={`staff-status-empty-${userStat.user_id}`}>
                    No {statusFilter} quotations
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {visible.map((q) => {
                // Colour-code the card by outcome so a
                // salesperson's row tells a story at-a-glance.
                //  • pending  → warm yellow tint  (still open)
                //  • closed   → soft green tint   (won)
                //  • lost     → soft red tint     (lost, with reason chip)
                const st = q._norm_status;
                const stateStyles = st === 'closed'
                  ? { card: 'bg-[#F0F7EA] border-[#8FBF6A] hover:border-[#556B2F]', chip: 'bg-[#556B2F]/15 text-[#3D5320]', label: '✓ Won' }
                  : st === 'lost'
                    ? { card: 'bg-[#FDF3F3] border-[#E5B7B7] hover:border-[#C44A4A]', chip: 'bg-[#C44A4A]/15 text-[#8B2E2E]', label: '✕ Lost' }
                    : { card: 'bg-[#FFFBEC] border-[#E9D8A6] hover:border-[#DAA520]', chip: 'bg-[#DAA520]/15 text-[#8B6914]', label: '● Pending' };
                return (
                  <button
                    key={q.id}
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    className={`border rounded-lg p-3 text-left hover:shadow-sm transition-all group ${stateStyles.card}`}
                    data-testid={`staff-quotation-card-${q.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs font-bold text-[#556B2F] group-hover:underline">
                        {q.quotation_number}
                      </span>
                      <ChevronRight className="w-4 h-4 text-[#8F9485] group-hover:text-[#556B2F]" />
                    </div>
                    <p className="text-sm font-medium text-[#1A1C16] truncate mb-1">
                      {q.customer_name || 'No name'}
                    </p>
                    <p className="text-xs text-[#8F9485] mb-1">
                      {q.customer_mobile || 'No phone'}
                    </p>
                    {/* Status chip — always present so the eye can quickly
                        find pending vs won vs lost even when scanning a
                        long list. */}
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded mb-2 ${stateStyles.chip}`}
                      data-testid={`staff-quotation-status-${q.id}`}
                    >
                      {stateStyles.label}
                    </span>
                    {/* Show "Created for" if this quotation was made for a salesperson */}
                    {q.created_for_name && (
                      <p className="text-xs text-[#556B2F] bg-[#556B2F]/10 px-2 py-0.5 rounded mb-2 inline-flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        For: {q.created_for_name} {q.created_for_code && `(${q.created_for_code})`}
                      </p>
                    )}
                    {/* Lost reason chip — only when status=lost AND a reason
                        was recorded. Uses `title` for a hover tooltip in case
                        the reason is longer than the truncated line. */}
                    {st === 'lost' && q.lost_reason && (
                      <p
                        className="text-[11px] text-[#8B2E2E] bg-[#C44A4A]/10 border border-[#C44A4A]/30 rounded px-2 py-1 mb-2 truncate"
                        title={q.lost_reason}
                        data-testid={`staff-quotation-lost-reason-${q.id}`}
                      >
                        <span className="font-semibold">Why lost:</span> {q.lost_reason}
                      </p>
                    )}
                    <div className={`flex items-center justify-between pt-2 border-t ${st === 'closed' ? 'border-[#8FBF6A]/40' : st === 'lost' ? 'border-[#E5B7B7]/60' : 'border-[#E9D8A6]/60'}`}>
                      <span className="font-mono-data text-sm font-bold text-[#DAA520]">
                        ₹{Math.ceil(q.grand_total).toLocaleString('en-IN')}
                      </span>
                      <span className="text-[10px] text-[#8F9485]">
                        {formatDate(q.created_at)}
                      </span>
                    </div>
                  </button>
                );
              })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
